import moment from "moment-timezone";
import PropTypes from "prop-types";
import { map, reduce } from "lodash";
import { Modal } from "antd";
import React, { useEffect, useState } from "react";

import { RequirementTable } from "./table";
import { ModelEnum, RequirementType } from "./types";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useRequirements } from "./hooks";
import { isDefined } from "@clipboard-health/util-ts";

function RequirementEditor({ initial, model, onFinish, visible }) {
  const { data } = useRequirements();
  const [expiries, setExpiries] = useState({});
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [initialKeys, setInitialKeys] = useState<any[]>([]);
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [nonExpiringRequirements, setNonExpiringRequirements] = useState(new Set());

  useEffect(() => {
    initState(initial);
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const getExpiries = () => {
    const add = (acc, cur) => ({
      ...acc,
      [cur.requirement._id]: cur.expiry && moment(cur.expiry),
    });
    return reduce(initial, add, {});
  };

  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const initState = (initial) => {
    if (!initial) {
      return;
    }

    const keys = map(initial, "requirement._id");
    setInitialKeys(keys);
    setSelectedRowKeys(keys);

    if (model !== ModelEnum.HCP) {
      return;
    }

    setExpiries(getExpiries());
  };

  const handleCancel = () => {
    onFinish({});
    setSelectedRowKeys(initialKeys);
  };

  const handleExpiryChange = (reqId) => (date) => {
    setExpiries((prev) => ({ ...prev, [reqId]: date }));
  };

  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const getFilteredExpiries = (prev, selectedRowKeys) => {
    const selectedKeysSet = new Set(selectedRowKeys);
    const add = (acc, val, key) => (selectedKeysSet.has(key) ? { ...acc, [key]: val } : acc);

    return reduce(prev, add, {});
  };

  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const handleSelectionChange = (selectedRowKeys) => {
    setSelectedRowKeys(selectedRowKeys);

    if (model !== ModelEnum.HCP) {
      return;
    }

    setExpiries((prev) => getFilteredExpiries(prev, selectedRowKeys));
  };

  const getRemovedRequirementIds = (selectedKeysSet) => {
    const removed = initial.filter((rec) => !selectedKeysSet.has(rec.requirement._id));
    return map(removed, "_id");
  };

  const getHcfResult = () => {
    if (model !== ModelEnum.HCF) {
      return;
    }

    const initialKeysSet = new Set(initialKeys);
    const selectedKeysSet = new Set(selectedRowKeys);

    const newRequirements = selectedRowKeys.filter((key) => !initialKeysSet.has(key));
    const removedRequirements = getRemovedRequirementIds(selectedKeysSet);

    return { newRequirements, removedRequirements };
  };

  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const getHcpResult = ({ nonExpiringRequirements = new Set() }) => {
    if (model !== ModelEnum.HCP) {
      return;
    }

    const initialKeysSet = new Set(initialKeys);
    const selectedKeysSet = new Set(selectedRowKeys);
    const newRequirementIds = selectedRowKeys.filter((key) => !initialKeysSet.has(key));

    const newRequirements = newRequirementIds.map((id) => ({
      requirementId: id,
      expiry: !nonExpiringRequirements.has(id) ? expiries[id] : null,
    }));

    const removedRequirements = getRemovedRequirementIds(selectedKeysSet);

    const updatedRequirements = initial
      .filter(
        (rec) =>
          selectedKeysSet.has(rec.requirement._id) &&
          !nonExpiringRequirements.has(rec?.requirement?._id) &&
          !moment(rec.expiry).isSame(expiries[rec.requirement._id])
      )
      .map((rec) => ({ _id: rec._id, expiry: expiries[rec.requirement._id] }));

    return { newRequirements, removedRequirements, updatedRequirements };
  };

  const handleOk = async () => {
    if (!initial) {
      onFinish({});
      return;
    }

    if (
      model === ModelEnum.HCP &&
      selectedRowKeys.find((key) => {
        const requirement =
          isDefined(data) && isDefined(data.allRequirements)
            ? data.allRequirements.find((r) => r._id === key)
            : undefined;
        // skip archived
        if (!requirement || requirement?.archived) {
          return false;
        }
        return !nonExpiringRequirements.has(key) && !expiries[key];
      })
    ) {
      showErrorToast("Heads up: one or more requirements are missing expiry dates");
      return;
    }

    const result = getHcfResult() || getHcpResult({ nonExpiringRequirements });

    onFinish({ ...result, selectedRowKeys, nonExpiringRequirements });
  };

  const title =
    model === ModelEnum.HCF
      ? "Set Requirements for Workplace"
      : "Set Requirements Fulfilled for Agent";

  return (
    <Modal
      destroyOnClose={true}
      onOk={handleOk}
      onCancel={handleCancel}
      title={title}
      visible={visible}
      width={800}
      wrapClassName="sticky-footer-modal"
    >
      <RequirementTable
        expiries={expiries}
        handleExpiryChange={handleExpiryChange}
        handleSelectionChange={handleSelectionChange}
        model={model}
        selectedRowKeys={selectedRowKeys}
        setNonExpiringRequirements={setNonExpiringRequirements}
      />
    </Modal>
  );
}

RequirementEditor.propTypes = {
  initial: PropTypes.arrayOf(RequirementType),
  model: PropTypes.string.isRequired,
  onFinish: PropTypes.func,
  visible: PropTypes.bool,
};

RequirementEditor.defaultProps = {
  visible: false,
};

export { RequirementEditor };
